import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { HEALTH_TIPS } from '@/mocks/jeux-data';

const GRID = 20;
const CELL = 22;
const SPEED = 150;
const COUNTDOWN_SECONDS = 3;

const BOARD_SIZE = GRID * CELL;

function randomPos(exclude: { x: number; y: number }[]) {
  let x: number;
  let y: number;
  let attempts = 0;
  do {
    x = Math.floor(Math.random() * GRID);
    y = Math.floor(Math.random() * GRID);
    attempts++;
  } while (exclude.some((p) => p.x === x && p.y === y) && attempts < 500);
  return { x, y };
}

const FOODS = [
  { icon: 'ri-apple-line', color: '#ef4444', points: 10, msg: 'Les fruits sont riches en vitamines !' },
  { icon: 'ri-leaf-line', color: '#22c55e', points: 10, msg: 'Mange 5 fruits et légumes par jour !' },
  { icon: 'ri-drop-line', color: '#3b82f6', points: 5, msg: "Bois 1,5L d'eau par jour !" },
  { icon: 'ri-capsule-line', color: '#f59e0b', points: 15, msg: 'Les vitamines renforcent ton immunité !' },
  { icon: 'ri-virus-line', color: '#dc2626', penalty: 1, msg: 'Évite les virus, lave-toi les mains !' },
  { icon: 'ri-bug-line', color: '#7c3aed', penalty: 1, msg: 'Les bactéries peuvent causer des infections.' },
  { icon: 'ri-restaurant-line', color: '#f97316', penalty: 0, msg: 'Limite la malbouffe pour ta santé !' },
] as const;

type FoodDef = (typeof FOODS)[number];

function randomFood(): FoodDef {
  return Math.random() > 0.4 ? FOODS[Math.floor(Math.random() * 4)] : FOODS[4 + Math.floor(Math.random() * 3)];
}

function isGood(f: FoodDef): f is (typeof FOODS)[0] {
  return 'points' in f;
}

export default function SnakeGame() {
  const navigate = useNavigate();
  const { startGame, endGame, gameScore, gameTime, gameErrors, updateScore, updateErrors, unlockAchievement } = useGame();

  // UI states
  const [snake, setSnake] = useState([{ x: 10, y: 10 }]);
  const [food, setFood] = useState(() => randomPos([{ x: 10, y: 10 }]));
  const [foodDef, setFoodDef] = useState<FoodDef>(FOODS[0]);
  const [message, setMessage] = useState<string | null>(null);
  const [showGameOver, setShowGameOver] = useState(false);
  const [screen, setScreen] = useState<'welcome' | 'playing'>('welcome');
  const [countdown, setCountdown] = useState(0);
  const [started, setStarted] = useState(false);

  // Mutable game state (single source of truth)
  const gameRef = useRef({
    snake: [{ x: 10, y: 10 }],
    food: { x: 0, y: 0 },
    foodDef: FOODS[0] as FoodDef,
    dir: { x: 0, y: 0 },
    nextDir: { x: 0, y: 0 },
    score: 0,
    errors: 0,
    active: false,
    started: false,
  });

  // Keep callbacks fresh
  const endGameRef = useRef(endGame);
  const updateScoreRef = useRef(updateScore);
  const updateErrorsRef = useRef(updateErrors);
  const unlockRef = useRef(unlockAchievement);
  const timeRef = useRef(gameTime);

  useEffect(() => { endGameRef.current = endGame; }, [endGame]);
  useEffect(() => { updateScoreRef.current = updateScore; }, [updateScore]);
  useEffect(() => { updateErrorsRef.current = updateErrors; }, [updateErrors]);
  useEffect(() => { unlockRef.current = unlockAchievement; }, [unlockAchievement]);
  useEffect(() => { timeRef.current = gameTime; }, [gameTime]);

  // Countdown
  useEffect(() => {
    if (screen !== 'playing' || countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [screen, countdown]);

  // Main game loop
  useEffect(() => {
    if (screen !== 'playing') return;
    const interval = setInterval(() => {
      const g = gameRef.current;
      if (!g.active || !g.started) return;

      // Apply queued direction
      g.dir = g.nextDir;
      const head = g.snake[0];
      const newHead = { x: head.x + g.dir.x, y: head.y + g.dir.y };

      // Wall collision
      if (newHead.x < 0 || newHead.x >= GRID || newHead.y < 0 || newHead.y >= GRID) {
        g.active = false;
        clearInterval(interval);
        endGameRef.current(g.score, timeRef.current, g.errors + 1);
        setShowGameOver(true);
        return;
      }

      // Self collision
      if (g.snake.some((seg) => seg.x === newHead.x && seg.y === newHead.y)) {
        g.active = false;
        clearInterval(interval);
        endGameRef.current(g.score, timeRef.current, g.errors + 1);
        setShowGameOver(true);
        return;
      }

      const newSnake = [newHead, ...g.snake];

      // Eat food
      if (newHead.x === g.food.x && newHead.y === g.food.y) {
        if (isGood(g.foodDef)) {
          g.score += g.foodDef.points;
          updateScoreRef.current(g.score);
          setMessage(g.foodDef.msg);
          if (g.score >= 90) unlockRef.current('snake_master');
        } else {
          g.errors += g.foodDef.penalty;
          updateErrorsRef.current(g.errors);
          setMessage(g.foodDef.msg);
          newSnake.pop();
          if (newSnake.length < 1) {
            g.active = false;
            clearInterval(interval);
            endGameRef.current(g.score, timeRef.current, g.errors);
            setShowGameOver(true);
            return;
          }
        }
        g.foodDef = randomFood();
        g.food = randomPos(newSnake);
        g.snake = newSnake;
        setSnake([...g.snake]);
        setFood({ ...g.food });
        setFoodDef({ ...g.foodDef });
        setTimeout(() => setMessage(null), 2000);
        return;
      }

      // Normal move
      newSnake.pop();
      g.snake = newSnake;
      setSnake([...g.snake]);
    }, SPEED);
    return () => clearInterval(interval);
  }, [screen]);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const g = gameRef.current;
      if (!g.active) return;

      const currentDir = (g.nextDir.x !== 0 || g.nextDir.y !== 0) ? g.nextDir : g.dir;
      let newDir = g.nextDir;
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          if (currentDir.y !== 1) newDir = { x: 0, y: -1 };
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          if (currentDir.y !== -1) newDir = { x: 0, y: 1 };
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          if (currentDir.x !== 1) newDir = { x: -1, y: 0 };
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          if (currentDir.x !== -1) newDir = { x: 1, y: 0 };
          break;
        default:
          return;
      }
      g.nextDir = newDir;
      if (!g.started && (newDir.x !== 0 || newDir.y !== 0)) {
        g.started = true;
        setStarted(true);
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleStart = () => {
    const initSnake = [{ x: 10, y: 10 }];
    const initFood = randomPos(initSnake);
    const initFoodDef = randomFood();

    gameRef.current = {
      snake: initSnake,
      food: initFood,
      foodDef: initFoodDef,
      dir: { x: 0, y: 0 },
      nextDir: { x: 0, y: 0 },
      score: 0,
      errors: 0,
      active: true,
      started: false,
    };

    setSnake(initSnake);
    setFood(initFood);
    setFoodDef(initFoodDef);
    setShowGameOver(false);
    setMessage(null);
    setStarted(false);
    setCountdown(COUNTDOWN_SECONDS);
    setScreen('playing');
    startGame('snake');
  };

  const handleReplay = () => {
    setScreen('welcome');
    setTimeout(() => {
      setShowGameOver(false);
      handleStart();
    }, 50);
  };

  const handleQuit = () => {
    gameRef.current.active = false;
    endGameRef.current(gameRef.current.score, timeRef.current, gameRef.current.errors);
    navigate('/jeux', { replace: true });
  };

  // Grid background style
  const gridBg = {
    backgroundColor: '#f8fafc',
    backgroundImage: `
      linear-gradient(to right, #e2e8f0 1px, transparent 1px),
      linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)
    `,
    backgroundSize: `${CELL}px ${CELL}px`,
  };

  if (screen === 'welcome') {
    return (
      <GameWrapper gameName="Snake Nutrition" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-apple-line text-3xl text-green-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Snake Nutrition</h2>
            <p className="text-foreground-600 text-sm mb-4">Mange les aliments sains, évite virus et malbouffe !</p>
            <p className="text-xs text-foreground-500 mb-6">Utilise les flèches du clavier ou WASD pour diriger le serpent</p>
            <button
              onClick={handleStart}
              className="w-full py-3 bg-primary-500 text-background-50 rounded-xl font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill mr-2"></i> Commencer
            </button>
          </div>
        </div>
      </GameWrapper>
    );
  }

  return (
    <GameWrapper gameName="Snake Nutrition" onQuit={handleQuit}>
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {/* Message banner */}
        {message && (
          <div className="bg-accent-100/70 border border-accent-200/50 rounded-xl px-4 py-2 mb-3 text-sm text-accent-900 text-center animate-in">
            {message}
          </div>
        )}

        {/* Countdown overlay */}
        {countdown > 0 && (
          <div className="mb-3 text-center">
            <div className="text-5xl font-black text-primary-500 animate-pulse">{countdown}</div>
            <p className="text-sm text-foreground-600 mt-1">Le jeu va bientôt commencer...</p>
          </div>
        )}

        {/* Waiting for first keypress */}
        {countdown === 0 && !started && (
          <div className="mb-3 text-center">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-semibold animate-pulse">
              <i className="ri-keyboard-line"></i>
              Appuie sur une flèche pour démarrer
            </div>
          </div>
        )}

        {/* Game board */}
        <div className="relative rounded-2xl border-2 border-slate-300 overflow-hidden shadow-lg" style={{ width: BOARD_SIZE, height: BOARD_SIZE, ...gridBg }}>
          {/* Food */}
          <div
            className="absolute flex items-center justify-center z-10"
            style={{ left: food.x * CELL, top: food.y * CELL, width: CELL, height: CELL }}
          >
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center animate-bounce"
              style={{ backgroundColor: foodDef.color }}
            >
              <i className={`${foodDef.icon} text-white text-xs`}></i>
            </div>
          </div>

          {/* Snake */}
          {snake.map((seg, i) => {
            const isHead = i === 0;
            return (
              <div
                key={i}
                className="absolute rounded-sm shadow-sm"
                style={{
                  left: seg.x * CELL + 1,
                  top: seg.y * CELL + 1,
                  width: CELL - 2,
                  height: CELL - 2,
                  backgroundColor: isHead ? '#f43f5e' : '#10b981',
                  border: isHead ? '2px solid #9f1239' : '1px solid #047857',
                  zIndex: isHead ? 20 : 10,
                  transition: 'none',
                }}
              >
                {/* Eyes on head */}
                {isHead && (
                  <div className="absolute inset-0 flex items-center justify-center gap-1">
                    <div className="w-1.5 h-1.5 bg-white rounded-full" />
                    <div className="w-1.5 h-1.5 bg-white rounded-full" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Mobile controls */}
        <div className="mt-4 grid grid-cols-3 gap-2 md:hidden">
          <div />
          <button
            onTouchStart={(e) => {
              e.preventDefault();
              const g = gameRef.current;
              if (!g.active) return;
              const currentDir = (g.nextDir.x !== 0 || g.nextDir.y !== 0) ? g.nextDir : g.dir;
              if (currentDir.y !== 1) g.nextDir = { x: 0, y: -1 };
              if (!g.started) { g.started = true; setStarted(true); }
            }}
            className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center active:bg-slate-200 cursor-pointer"
          >
            <i className="ri-arrow-up-line text-xl text-slate-700"></i>
          </button>
          <div />
          <button
            onTouchStart={(e) => {
              e.preventDefault();
              const g = gameRef.current;
              if (!g.active) return;
              const currentDir = (g.nextDir.x !== 0 || g.nextDir.y !== 0) ? g.nextDir : g.dir;
              if (currentDir.x !== 1) g.nextDir = { x: -1, y: 0 };
              if (!g.started) { g.started = true; setStarted(true); }
            }}
            className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center active:bg-slate-200 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-slate-700"></i>
          </button>
          <div className="w-14 h-14" />
          <button
            onTouchStart={(e) => {
              e.preventDefault();
              const g = gameRef.current;
              if (!g.active) return;
              const currentDir = (g.nextDir.x !== 0 || g.nextDir.y !== 0) ? g.nextDir : g.dir;
              if (currentDir.x !== -1) g.nextDir = { x: 1, y: 0 };
              if (!g.started) { g.started = true; setStarted(true); }
            }}
            className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center active:bg-slate-200 cursor-pointer"
          >
            <i className="ri-arrow-right-line text-xl text-slate-700"></i>
          </button>
          <div />
          <button
            onTouchStart={(e) => {
              e.preventDefault();
              const g = gameRef.current;
              if (!g.active) return;
              const currentDir = (g.nextDir.x !== 0 || g.nextDir.y !== 0) ? g.nextDir : g.dir;
              if (currentDir.y !== -1) g.nextDir = { x: 0, y: 1 };
              if (!g.started) { g.started = true; setStarted(true); }
            }}
            className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center active:bg-slate-200 cursor-pointer"
          >
            <i className="ri-arrow-down-line text-xl text-slate-700"></i>
          </button>
          <div />
        </div>
      </div>

      {showGameOver && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="snake"
          message={gameScore >= 100 ? 'Super nutritionniste !' : gameScore >= 50 ? 'Pas mal du tout !' : 'Partie terminée'}
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}